import React from 'react';
import { CheckCircle, Circle, ListChecks, ChevronLeft } from 'lucide-react';
import { Button } from '@heroui/react';
import { cn } from '@/utils/cn';
import { GuideStep } from '@/services/apis/guides';
import { GuideProgress } from '@/types';

interface GuideViewerStepsProps {
  steps: GuideStep[];
  currentStepIndex: number;
  progress: GuideProgress | null;
  isOpen: boolean;
  onToggle: () => void;
  onStepClick: (index: number) => void;
  getStepTranslation: (step: GuideStep) => any;
}

const GuideViewerSteps: React.FC<GuideViewerStepsProps> = ({
  steps,
  currentStepIndex,
  progress,
  isOpen,
  onToggle,
  onStepClick,
  getStepTranslation,
}) => {
  return (
    <div className={cn('hidden lg:flex flex-col transition-all duration-300', isOpen ? 'w-64' : 'w-12')}>
      <div className="bg-content1 rounded-lg border border-divider h-full overflow-hidden flex flex-col">
        <div
          className={`flex items-center  ${isOpen ? 'justify-between' : 'justify-center'} p-4 border-b border-divider`}
        >
          {isOpen && <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wide">Steps</h3>}
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={onToggle}
            aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ListChecks className="w-4 h-4" />}
          </Button>
        </div>
        {isOpen && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              {steps.map((step: GuideStep, index: number) => {
                const isCompleted = progress?.completedStepIds.includes(step.id) || false;
                const isCurrent = index === currentStepIndex;
                const stepTrans = getStepTranslation(step);

                return (
                  <Button
                    key={step.id}
                    onPress={() => onStepClick(index)}
                    className="w-full h-full flex items-start gap-3 px-3 py-3 rounded-lg transition-colors text-left"
                    variant={isCurrent ? 'solid' : 'flat'}
                    color={isCurrent ? 'primary' : 'default'}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium mb-0.5">Step {index + 1}</div>
                      <div className="text-sm font-medium line-clamp-2">{stepTrans?.title || 'Untitled'}</div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuideViewerSteps;
